import { sentryLog } from "../../../tools/sentry";
import { DomainInput } from "../../../schema/types/domain/input";
import { v4 as uuidv4 } from "uuid";
import { formSet, formSetValue, hasNestedPath } from "../../gvc/testNewForm/utils";
import { store } from "../../../store";
import { inputToDomain } from "./utils/utils";
import { homeLink, request } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";

export function getValidator(id: string) {
  return async function validator(v: DomainInput): Promise<boolean | null> {
    // setup

    const validationCallId = uuidv4();

    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `meta.validationCallId`, validationCallId);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});
    formSetValue(id, `meta.dryRun.message`, "");

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    // dry run
    try {
      const org = store.getState().context.org;
      const ctx = { org, gvc: "" };
      const body = inputToDomain(v);
      const method = v.meta.draftType === "edit" ? "patch" : "post";
      const domainLink = v.meta.domainLink || "";
      const url = v.meta.draftType === "edit" ? `${domainLink}?dryRun` : `${homeLink("domain", ctx)}?dryRun`;

      const ownership = {
        checkedName: v.basic.name,
        config: null,
        passed: true,
      };
      const dns = {
        config: null,
      };
      const apex: {
        config: null | { message: string; apex: string };
      } = {
        config: null,
      };

      if (v.meta.mode === "basic") {
        const res = await request({
          method: method,
          url: url,
          body: body,
          dontThrowOnFailure: true,
        });

        const form = store.getState().form.instances[id];
        const _validationCallId = form.meta.validationCallId;
        if (_validationCallId !== validationCallId) {
          console.log("skipping because its an old call");
          return null;
        }

        try {
          const status = res.status;
          const data = res.data;
          console.log("Dryrun Basic Start | method", method, "status", status);
          console.log(data);
          if (status === 400 && hasNestedPath(data, "details.0.path")) {
            let path = data.details[0].path.join(".");
            const message = data.details[0].message;

            const ownershipBlockingProperties = ["workloadLink", "name", "description"];
            for (const ownershipBlockingProperty of ownershipBlockingProperties) {
              if (path.includes(ownershipBlockingProperty)) {
                ownership.passed = false;
              }
            }

            if (path.includes("workloadLink")) {
              path = "workload";
            }

            setError(`basic.${path}`, message);
            console.log("schema error: path:", path, `message:`, message);
          } else if (status === 400 && data.code && data.code === "must_prove_ownership") {
            ownership.passed = false;
            ownership.config = data.details.ownershipConfig;
          } else if (status === 400 && data.message && data.message.toLowerCase().includes("already exists")) {
            ownership.passed = false;
            setError(`basic.name`, data.message);
          }

          if (data.details?.ownershipConfig || data.details?.dnsConfig) {
            console.log("setting message", data.message, data);
            formSetValue(id, `meta.dryRun.message`, data.message);
          }

          if (data.details?.dnsConfig) {
            dns.config = data.details?.dnsConfig;
          }
          formSetValue(id, `meta.dryRun.dns`, dns.config);

          formSetValue(id, `meta.dryRun.ownership`, ownership);
          console.log("Dryrun Basic End");
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          console.error("Failed to process dryrun", errorMessage);
        }
      }

      // TODO apex, also try apex for basic when can it be needed?
      if (v.meta.mode === "advanced") {
        const s = v.advanced.spec;

        ownership.checkedName = v.advanced.name;
        // name only to get past ownership check
        let res = await request({
          method: method,
          url: url,
          body: { name: v.advanced.name },
          dontThrowOnFailure: true,
        });

        const form = store.getState().form.instances[id];
        const _validationCallId = form.meta.validationCallId;
        if (_validationCallId !== validationCallId) {
          console.log("skipping because its an old call");
          return null;
        }

        try {
          const status = res.status;
          const data = res.data;
          console.log("DryRun Advanced NameOnly Start | method", method, "status", status);
          console.log(data);
          if (status === 400 && hasNestedPath(data, "details.0.path")) {
            let path = data.details[0].path.join(".");
            const message = data.details[0].message;

            if (path === "name") {
              ownership.passed = false;
            }

            if (path.includes("workloadLink")) {
              path = path.replace("workloadLink", "workload");
            }

            setError(`advanced.${path}`, message);
            console.log("schema error: path:", path, `message:`, message);
          } else if (status === 400 && data.code && data.code === "must_prove_ownership") {
            ownership.passed = false;
            ownership.config = data.details.ownershipConfig;
          } else if (status === 400 && data.message && data.message.toLowerCase().includes("already exists")) {
            ownership.passed = false;
            setError(`advanced.name`, data.message);
          }

          formSetValue(id, `meta.dryRun.apex`, apex.config);

          if (data.details?.ownershipConfig || data.details?.dnsConfig) {
            console.log("setting message", data.message, data);
            formSetValue(id, `meta.dryRun.message`, data.message);
          }

          if (data.details?.dnsConfig) {
            dns.config = data.details?.dnsConfig;
          }
          formSetValue(id, `meta.dryRun.dns`, dns.config);

          console.log("DryRun Advanced NameOnly End");
          formSetValue(id, `meta.dryRun.ownership`, ownership);
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          console.error("Failed to process dryrun", errorMessage);
        }

        if (ownership.passed) {
          res = await request({
            method: method,
            url: url,
            body: body,
            dontThrowOnFailure: true,
          });

          const form = store.getState().form.instances[id];
          const _validationCallId = form.meta.validationCallId;
          if (_validationCallId !== validationCallId) {
            console.log("skipping because its an old call");
            return null;
          }

          try {
            const status = res.status;
            const data = res.data;
            console.log("DryRun Advanced Start | method", method, "status", status);
            console.log(data);
            if (status === 400 && hasNestedPath(data, "details.0.path")) {
              let path = data.details[0].path.join(".");
              const message = data.details[0].message;

              if (path.includes("workloadLink")) {
                path = path.replace("workloadLink", "workload");
              }

              // Route spec-level errors to root error display
              if (path === "spec") {
                setError("root", message);
              } else {
                setError(`advanced.${path}`, message);
              }
              console.log("schema error: path:", path, `message:`, message);
            } else if (status === 400 && data.code && data.code === "apex_must_exist") {
              apex.config = { apex: data.details?.apex, message: data.message };
            }
            formSetValue(id, `meta.dryRun.apex`, apex.config);

            if (data.details?.dnsConfig) {
              dns.config = data.details?.dnsConfig;
            }
            formSetValue(id, `meta.dryRun.dns`, dns.config);

            console.log("DryRun Advanced End");
          } catch (e) {
            let errorMessage = e?.response?.data?.message;
            if (!errorMessage) errorMessage = e.message;
            console.error("Failed to process dryrun failure result", errorMessage);
          }
        }

        if (s.routingMode === "gvcSubdomain" || s.routingMode === "pathBased") {
          if (!v.gvc?.link) {
            setError(`gvc`, "GVC is required");
          } else {
            const parsedLink = parseLink(v.gvc.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`gvc`, parsedLink.errorMessage);
            }
          }
        }
        if (s.routingMode === "workloadSubdomain") {
          if (!s.workload?.link) {
            setError(`advanced.spec.workload`, "Workload is required");
          } else {
            const parsedLink = parseLink(s.workload.link, { useInputCtx: true });
            if (!parsedLink.isValid) {
              setError(`advanced.spec.workload`, parsedLink.errorMessage);
            }
          }
        }

        // TODO clientCert serverCert

        for (const portIndexStr in s.ports) {
          const portIndex = Number(portIndexStr);
          const port = s.ports[portIndex];

          if (port.tls.clientCertificate.isEnabled) {
            if (port.tls.clientCertificate.secret.value) {
              const parsedLink = parseLink(port.tls.clientCertificate.secret.link, { useInputCtx: true });
              if (!parsedLink.isValid) {
                setError(`advanced.spec.ports.${portIndex}.tls.clientCertificate.secret`, parsedLink.errorMessage);
              }
            }
          }

          if (port.tls.serverCertificate.isEnabled) {
            if (port.tls.serverCertificate.secret.value) {
              const parsedLink = parseLink(port.tls.serverCertificate.secret.link, { useInputCtx: true });
              if (!parsedLink.isValid) {
                setError(`advanced.spec.ports.${portIndex}.tls.serverCertificate.secret`, parsedLink.errorMessage);
              }
            }
          }

          for (const allowOriginIndexStr in port.cors.allowOrigins) {
            const allowOriginIndex = Number(allowOriginIndexStr);
            const allowOrigin = port.cors.allowOrigins[allowOriginIndex];

            if (!allowOrigin.type) {
              setError(
                `advanced.spec.ports.${portIndex}.cors.allowOrigins.${allowOriginIndex}.type`,
                `Type is required`,
              );
              setError(`advanced.spec.ports.${portIndex}.cors.allowOriginsroot`, `Types are required`);
            }
            if (!allowOrigin.value) {
              setError(
                `advanced.spec.ports.${portIndex}.cors.allowOrigins.${allowOriginIndex}.value`,
                `Value is required`,
              );
              setError(`advanced.spec.ports.${portIndex}.cors.allowOriginsroot`, `Values are required`);
            }
          }

          for (const methodIndexStr in port.cors.allowMethods) {
            const methodIndex = Number(methodIndexStr);
            const method = port.cors.allowMethods[methodIndex];

            if (!method.value) {
              setError(`advanced.spec.ports.${portIndex}.cors.allowMethods.${methodIndex}.value`, `Value is required`);
              setError(`advanced.spec.ports.${portIndex}.cors.allowMethodsroot`, `Values are required`);
            }
          }

          for (const headerIndexStr in port.cors.allowHeaders) {
            const headerIndex = Number(headerIndexStr);
            const header = port.cors.allowHeaders[headerIndex];

            if (!header.value) {
              setError(`advanced.spec.ports.${portIndex}.cors.allowHeaders.${headerIndex}.value`, `Value is required`);
              setError(`advanced.spec.ports.${portIndex}.cors.allowHeadersroot`, `Values are required`);
            }
          }

          for (const headerIndexStr in port.cors.exposeHeaders) {
            const headerIndex = Number(headerIndexStr);
            const header = port.cors.exposeHeaders[headerIndex];

            if (!header.value) {
              setError(`advanced.spec.ports.${portIndex}.cors.exposeHeaders.${headerIndex}.value`, `Value is required`);
              setError(`advanced.spec.ports.${portIndex}.cors.exposeHeadersroot`, `Values are required`);
            }
          }

          for (const routeIndexStr in port.routes) {
            const routeIndex = Number(routeIndexStr);
            const route = port.routes[routeIndex];

            for (const mirrorIndexStr in route.mirror || []) {
              const mirrorIndex = Number(mirrorIndexStr);
              const mirror = route.mirror[mirrorIndex];

              if (!mirror.workload.value) {
                setError(
                  `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.workload`,
                  "Workload is required",
                );
              } else {
                const parsedLink = parseLink(mirror.workload.link, { useInputCtx: true });
                if (!parsedLink.isValid) {
                  setError(
                    `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.workload`,
                    parsedLink.errorMessage,
                  );
                }
              }

              if (!mirror.percent || Number(mirror.percent) < 0 || Number(mirror.percent) > 100) {
                setError(
                  `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.percent`,
                  "Percent must be between 0 and 100",
                );
              }

              if (mirror.port && mirror.port.length > 0) {
                const portNum = Number(mirror.port);
                if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
                  setError(
                    `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.port`,
                    "Port must be between 1 and 65535",
                  );
                }
              }
            }
          }
        }
      }

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0 || !ownership.passed;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      sentryLog("domain validator error", { "data.error": e.message });
      formSet(id, `errors`, { root: { message: "An unexpected error occurred. This has been logged and we will fix it." } });
      return true;
    }
  };
}
